/**
 * Chatwoot Client API Service
 */

// ============ CONFIGURATION ============
const CHATWOOT_BASE_URL = process.env.REACT_APP_CHATWOOT_BASE_URL || 'https://chatwoot.cms.chargingadda.com';
const INBOX_IDENTIFIER = process.env.REACT_APP_CHATWOOT_INBOX_IDENTIFIER || 'pnQuNvfCugwvcc5zZJxAMyae';
const BACKEND_URL = process.env.REACT_APP_CHATWOOT_BACKEND_URL || 'https://api.cms.chargingadda.com/v1';
const ADMIN_API_URL = (process.env.REACT_APP_API_URL || 'https://admin-api.cms.chargingadda.com').replace(/\/$/, '');

export const CHATWOOT_CONFIG = {
  BASE_URL: CHATWOOT_BASE_URL,
  INBOX_IDENTIFIER,
  BACKEND_URL,
  ADMIN_API_URL,
  WS_URL: CHATWOOT_BASE_URL.replace('https://', 'wss://').replace('http://', 'ws://') + '/cable',
  API_URL: `${CHATWOOT_BASE_URL}/public/api/v1/inboxes/${INBOX_IDENTIFIER}`,
} as const;

// ============ TYPES ============
export interface ChatwootContact {
  id: number;
  source_id: string;
  name: string;
  email: string;
  pubsub_token: string;
}

export interface ChatwootMessageSender {
  id: number;
  name: string;
  avatar?: string;
  type: 'contact' | 'user';
}

export interface ChatwootAttachment {
  id: number;
  message_id: number;
  file_type: string;
  account_id: number;
  extension: string;
  data_url: string;
  thumb_url: string;
  file_size: number;
}

export interface ChatwootMessage {
  id: number | string;
  content: string;
  message_type: 'incoming' | 'outgoing' | number;
  content_type: string;
  content_attributes: Record<string, unknown>;
  created_at: number | string;
  conversation_id: number;
  attachments: ChatwootAttachment[];
  sender: ChatwootMessageSender;
  private?: boolean;
  status?: 'sending' | 'sent' | 'delivered' | 'failed';
  echo_id?: string;
}

export interface ChatwootConversationContact {
  id: number;
  name: string;
  email?: string;
}

export interface ChatwootConversationMeta {
  assignee?: {
    id: number;
    name: string;
    avatar_url?: string;
  };
}

export interface ChatwootConversation {
  id: number;
  inbox_id: number | string;
  status: 'open' | 'resolved' | 'pending' | 'snoozed';
  messages: ChatwootMessage[];
  contact: ChatwootConversationContact;
  created_at?: number;
  updated_at?: number;
  unread_count?: number;
  agent_last_seen_at?: number;
  contact_last_seen_at?: number;
  custom_attributes?: Record<string, unknown>;
  additional_attributes?: Record<string, unknown>;
  labels?: string[];
  meta?: ChatwootConversationMeta;
}

export interface CreateContactPayload {
  identifier: string;
  name: string;
  email?: string;
  phone_number?: string;
  custom_attributes?: Record<string, unknown>;
}

export interface CreateMessagePayload {
  content: string;
  echo_id?: string;
}

export interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
}

// ============ PHONE NUMBER FORMATTING ============
export function formatPhoneToE164(phone: string | undefined, defaultCountryCode = '91'): string {
  if (!phone) return '';
  let cleaned = phone.replace(/[^\d+]/g, '');
  if (cleaned.startsWith('+')) return cleaned;
  cleaned = cleaned.replace(/^0+/, '');
  if (cleaned.startsWith(defaultCountryCode) && cleaned.length > 10) {
    return `+${cleaned}`;
  }
  return `+${defaultCountryCode}${cleaned}`;
}

// ============ STORAGE ============
const STORAGE_KEYS = {
  USER_ID: 'chatwoot_user_id',
  PUBSUB_TOKEN: 'chatwoot_pubsub_token',
  SOURCE_ID: 'chatwoot_source_id',
} as const;

export const ChatwootStorage = {
  saveSourceId(sourceId: string): void {
    try {
      localStorage.setItem(STORAGE_KEYS.SOURCE_ID, sourceId);
    } catch (e) {
      console.error('ChatwootStorage: Failed to save source_id', e);
    }
  },

  getSourceId(): string | null {
    try {
      return localStorage.getItem(STORAGE_KEYS.SOURCE_ID);
    } catch {
      return null;
    }
  },

  saveUserId(userId: string): void {
    try {
      localStorage.setItem(STORAGE_KEYS.USER_ID, userId);
    } catch (e) {
      console.error('ChatwootStorage: Failed to save user_id', e);
    }
  },

  getUserId(): string | null {
    try {
      return localStorage.getItem(STORAGE_KEYS.USER_ID);
    } catch {
      return null;
    }
  },

  savePubsubToken(token: string): void {
    try {
      localStorage.setItem(STORAGE_KEYS.PUBSUB_TOKEN, token);
    } catch (e) {
      console.error('ChatwootStorage: Failed to save pubsub_token', e);
    }
  },

  getPubsubToken(): string | null {
    try {
      return localStorage.getItem(STORAGE_KEYS.PUBSUB_TOKEN);
    } catch {
      return null;
    }
  },

  clearAll(): void {
    try {
      Object.values(STORAGE_KEYS).forEach((key) => localStorage.removeItem(key));
    } catch {
      // Ignore
    }
  },
};

// ============ ADMIN BACKEND — SOURCE ID ============

export type FetchSourceIdResult =
  | { status: 'found'; sourceId: string }
  | { status: 'not_found' }      // 404 — safe to create
  | { status: 'error' };         // other failure — do NOT create

/**
 * GET /api/support/chatwoot/source-id
 * Returns the stored source_id for the current admin (identified from JWT).
 * Distinguishes 404 (not_found — safe to create) from other errors.
 */
export async function fetchSourceIdFromBackend(): Promise<FetchSourceIdResult> {
  const token = localStorage.getItem('access_token');
  if (!token) return { status: 'error' };

  try {
    const response = await fetch(
      `${CHATWOOT_CONFIG.ADMIN_API_URL}/api/support/chatwoot/source-id`,
      {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    if (response.status === 404) {
      console.log('[fetchSourceIdFromBackend] No existing source_id (404)');
      return { status: 'not_found' };
    }

    if (!response.ok) {
      console.warn(`[fetchSourceIdFromBackend] Unexpected status ${response.status}`);
      return { status: 'error' };
    }

    const data = await response.json() as { source_id: string };
    console.log('[fetchSourceIdFromBackend] ✅ Got source_id:', data.source_id);
    return { status: 'found', sourceId: data.source_id };
  } catch (err) {
    console.warn('[fetchSourceIdFromBackend] Network error:', err);
    return { status: 'error' };
  }
}

/**
 * POST /api/support/chatwoot/source-id
 * Persists the source_id for the current admin. First-write-wins — the
 * server returns the authoritative source_id (which may differ if a
 * concurrent request already stored one).
 */
export async function persistSourceIdToBackend(sourceId: string): Promise<string | null> {
  const token = localStorage.getItem('access_token');
  if (!token) return null;

  try {
    const response = await fetch(
      `${CHATWOOT_CONFIG.ADMIN_API_URL}/api/support/chatwoot/source-id`,
      {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ source_id: sourceId }),
      }
    );

    if (!response.ok) {
      console.warn(`[persistSourceIdToBackend] Unexpected status ${response.status}`);
      return null;
    }

    const data = await response.json() as { source_id: string };
    // Trust the server's returned value (first-write-wins)
    console.log('[persistSourceIdToBackend] ✅ Server confirmed source_id:', data.source_id);
    return data.source_id ?? null;
  } catch (err) {
    console.warn('[persistSourceIdToBackend] Network error:', err);
    return null;
  }
}

// ============ API HELPERS ============
async function apiRequest<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<ApiResponse<T>> {
  try {
    const url = `${CHATWOOT_CONFIG.API_URL}${endpoint}`;
    
    // Get pubsub token for authentication
    const pubsubToken = ChatwootStorage.getPubsubToken() ?? currentPubsubToken;
    
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };
    
    // Add existing headers
    if (options.headers) {
      const existingHeaders = options.headers as Record<string, string>;
      Object.assign(headers, existingHeaders);
    }
    
    // Add authentication header for public API
    if (pubsubToken) {
      headers['X-Auth-Token'] = pubsubToken;
    }
    
    console.log(`[Chatwoot API] ${options.method || 'GET'} ${url}`, { pubsubToken: !!pubsubToken });
    
    if (options.body) {
      try {
        const bodyObj = JSON.parse(options.body as string);
        console.log('[Chatwoot API] Request body:', JSON.stringify(bodyObj, null, 2));
        console.log('[Chatwoot API] Has custom_attributes?', 'custom_attributes' in bodyObj);
      } catch {
        console.log('[Chatwoot API] Request body (raw):', options.body);
      }
    }
    
    const fetchOptions = {
      ...options,
      headers,
    };

    const response = await fetch(url, fetchOptions);

    if (!response.ok) {
      const errorText = await response.text();
      console.error(`❌ Chatwoot API Error [${response.status}] ${options.method || 'GET'} ${url}`, {
        status: response.status,
        statusText: response.statusText,
        error: errorText,
      });
      return {
        success: false,
        error: `API Error: ${response.status} - ${errorText}`,
      };
    }

    const contentLength = response.headers.get('content-length');
    const contentType = response.headers.get('content-type');

    if (response.status === 204 || contentLength === '0' || !contentType?.includes('application/json')) {
      return { success: true, data: undefined as unknown as T };
    }

    const text = await response.text();
    if (!text || text.trim() === '') {
      console.log(`[Chatwoot API] Response is empty, returning undefined`);
      return { success: true, data: undefined as unknown as T };
    }

    const data = JSON.parse(text) as T;
    console.log(`[Chatwoot API] Response received:`, {
      endpoint,
      dataType: Array.isArray(data) ? 'array' : typeof data,
      dataLength: Array.isArray(data) ? data.length : 'N/A',
      ...(typeof data === 'object' && data !== null && {
        keys: Object.keys(data as object),
        custom_attributes: (data as any)?.custom_attributes,
      }),
    });
    return { success: true, data };
  } catch (error) {
    console.error('Chatwoot API Request Failed:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Network request failed',
    };
  }
}

// ============ MODULE STATE ============
let currentUserId: string | null = null;
let currentPubsubToken: string | null = null;

export function setCurrentUserId(userId: string | null): void {
  currentUserId = userId;
  if (userId) {
    ChatwootStorage.saveUserId(userId);
  }
}

export function getCurrentUserId(): string | null {
  return currentUserId ?? ChatwootStorage.getUserId();
}

export function getSourceId(userIdParam?: string): string | null {
  // Priority: provided param > currentContact.source_id > module state > localStorage
  if (userIdParam) return userIdParam;
  if (currentContact?.source_id) return currentContact.source_id;
  return getCurrentUserId();
}

export function setCurrentPubsubToken(token: string | null): void {
  currentPubsubToken = token;
  if (token) {
    ChatwootStorage.savePubsubToken(token);
  }
}

export function getCurrentContact(): ChatwootContact | null {
  const userId = getCurrentUserId();
  if (!userId) return null;
  
  return {
    id: 1,
    source_id: userId,
    name: 'User',
    email: '',
    pubsub_token: ChatwootStorage.getPubsubToken() ?? currentPubsubToken ?? '',
  };
}

// ============ CONTACT APIs ============
let currentContact: ChatwootContact | null = null;

export function setCurrentContact(contact: ChatwootContact | null): void {
  currentContact = contact;
  if (contact) {
    setCurrentUserId(contact.source_id);
    setCurrentPubsubToken(contact.pubsub_token);
  }
}

export async function createOrGetContact(
  payload: CreateContactPayload
): Promise<ApiResponse<ChatwootContact>> {
  const formattedPayload: CreateContactPayload = {
    ...payload,
    phone_number: formatPhoneToE164(payload.phone_number),
  };

  console.log('[createOrGetContact] Creating contact:', { identifier: payload.identifier, name: payload.name });

  const result = await apiRequest<ChatwootContact>('/contacts', {
    method: 'POST',
    body: JSON.stringify(formattedPayload),
  });

  if (result.success && result.data) {
    console.log('[createOrGetContact] ✅ Contact created with source_id:', result.data.source_id);
    currentContact = result.data;
    setCurrentUserId(result.data.source_id);
    setCurrentPubsubToken(result.data.pubsub_token);
  } else {
    console.error('[createOrGetContact] ❌ Failed:', result.error);
  }

  return result;
}

export async function getContact(
  contactIdentifier: string
): Promise<ApiResponse<ChatwootContact>> {
  return apiRequest<ChatwootContact>(`/contacts/${contactIdentifier}`);
}

export async function updateContact(
  contactIdentifier: string,
  payload: Partial<CreateContactPayload>
): Promise<ApiResponse<ChatwootContact>> {
  const formattedPayload = {
    ...payload,
    phone_number: payload.phone_number ? formatPhoneToE164(payload.phone_number) : undefined,
  };
  
  return apiRequest<ChatwootContact>(`/contacts/${contactIdentifier}`, {
    method: 'PATCH',
    body: JSON.stringify(formattedPayload),
  });
}

// ============ CONVERSATION APIs ============
export async function getConversations(
  userId?: string
): Promise<ApiResponse<ChatwootConversation[]>> {
  // Use provided userId, or get from currentContact.source_id, or fallback to module state
  let sourceId: string | null = userId ?? null;
  
  if (!sourceId && currentContact?.source_id) {
    sourceId = currentContact.source_id;
  }
  
  if (!sourceId) {
    sourceId = getCurrentUserId();
  }

  if (!sourceId) {
    return { success: false, error: 'No source_id available. Call createOrGetContact first.' };
  }

  console.log('[getConversations] Fetching with source_id:', sourceId);

  const result = await apiRequest<ChatwootConversation[] | { conversations: ChatwootConversation[] }>(
    `/contacts/${sourceId}/conversations`
  );

  if (!result.success) {
    return result as ApiResponse<ChatwootConversation[]>;
  }

  // Handle both direct array and wrapped object responses
  let conversations: ChatwootConversation[] = [];
  
  if (Array.isArray(result.data)) {
    conversations = result.data;
  } else if (result.data && typeof result.data === 'object' && 'conversations' in result.data) {
    conversations = (result.data as { conversations: ChatwootConversation[] }).conversations;
  }

  console.log('[getConversations] API returned:', {
    rawData: result.data,
    parsedConversations: conversations,
    count: conversations.length,
  });

  return { success: true, data: conversations };
}

export async function getConversation(
  conversationId: number,
  userId?: string
): Promise<ApiResponse<ChatwootConversation>> {
  const sourceId = getSourceId(userId);

  if (!sourceId) {
    return { success: false, error: 'No user_id available.' };
  }

  return apiRequest<ChatwootConversation>(
    `/contacts/${sourceId}/conversations/${conversationId}`
  );
}

export async function createConversation(
  customAttributes?: Record<string, unknown>,
  userId?: string
): Promise<ApiResponse<ChatwootConversation>> {
  const sourceId = getSourceId(userId);

  if (!sourceId) {
    console.error('createConversation: No user_id available');
    return { success: false, error: 'No user_id available. Call createOrGetContact first.' };
  }

  const attrs = customAttributes ?? {};
  console.log('[createConversation] Creating conversation', {
    sourceId,
    custom_attributes: attrs,
    endpoint: `/contacts/${sourceId}/conversations`,
  });

  return apiRequest<ChatwootConversation>(`/contacts/${sourceId}/conversations`, {
    method: 'POST',
    body: JSON.stringify({ custom_attributes: attrs }),
  });
}

export async function updateLastSeen(
  conversationId: number,
  userId?: string
): Promise<ApiResponse<void>> {
  const sourceId = getSourceId(userId);

  if (!sourceId) {
    return { success: false, error: 'No user_id available.' };
  }

  return apiRequest<void>(
    `/contacts/${sourceId}/conversations/${conversationId}/update_last_seen`,
    { method: 'POST' }
  );
}

// ============ MESSAGE APIs ============
export async function getMessages(
  conversationId: number,
  userId?: string
): Promise<ApiResponse<ChatwootMessage[]>> {
  const sourceId = userId ?? getCurrentUserId();

  if (!sourceId) {
    return { success: false, error: 'No user_id available.' };
  }

  const result = await apiRequest<ChatwootMessage[] | { messages: ChatwootMessage[] }>(
    `/contacts/${sourceId}/conversations/${conversationId}/messages`
  );

  if (!result.success) {
    return result as ApiResponse<ChatwootMessage[]>;
  }

  // Handle both direct array and wrapped object responses
  let messages: ChatwootMessage[] = [];
  
  if (Array.isArray(result.data)) {
    messages = result.data;
  } else if (result.data && typeof result.data === 'object' && 'messages' in result.data) {
    messages = (result.data as { messages: ChatwootMessage[] }).messages;
  }

  console.log('[getMessages] API returned:', {
    conversationId,
    count: messages.length,
  });

  return { success: true, data: messages };
}

export async function sendMessage(
  conversationId: number,
  content: string,
  echoId?: string,
  userId?: string
): Promise<ApiResponse<ChatwootMessage>> {
  const sourceId = userId ?? getCurrentUserId();

  console.log('[sendMessage] Sending message for user_id:', sourceId);

  if (!sourceId) {
    const error = 'No user_id available.';
    console.error('[sendMessage]', error);
    return { success: false, error };
  }

  const payload: CreateMessagePayload = {
    content,
    echo_id: echoId ?? `msg_${Date.now()}`,
  };

  const endpoint = `/contacts/${sourceId}/conversations/${conversationId}/messages`;
  const jsonPayload = JSON.stringify(payload);
  
  console.log('[sendMessage] Sending message:', {
    conversationId,
    content: content.substring(0, 50),
    endpoint,
  });

  return apiRequest<ChatwootMessage>(
    endpoint,
    {
      method: 'POST',
      body: jsonPayload,
    }
  );
}

export async function startNewConversation(
  initialMessage: string,
  customAttributes?: Record<string, unknown>,
  contactSourceId?: string
): Promise<ApiResponse<{ conversation: ChatwootConversation; message: ChatwootMessage }>> {
  const convResult = await createConversation(customAttributes, contactSourceId);

  if (!convResult.success || !convResult.data) {
    return { success: false, error: convResult.error ?? 'Failed to create conversation' };
  }

  const msgResult = await sendMessage(
    convResult.data.id,
    initialMessage,
    undefined,
    contactSourceId
  );

  if (!msgResult.success || !msgResult.data) {
    return { success: false, error: msgResult.error ?? 'Failed to send initial message' };
  }

  return {
    success: true,
    data: {
      conversation: convResult.data,
      message: msgResult.data,
    },
  };
}

// ============ WEBSOCKET ============
export type WebSocketEventType =
  | 'message.created'
  | 'message.updated'
  | 'conversation.created'
  | 'conversation.status_changed'
  | 'conversation.read'
  | 'conversation.typing_on'
  | 'conversation.typing_off'
  | 'presence.update';

export interface WebSocketEventData {
  conversation_id?: number;
  id?: number;
  status?: string;
  [key: string]: unknown;
}

export interface WebSocketEvent {
  event: WebSocketEventType;
  data: WebSocketEventData;
}

export type WebSocketCallback = (event: WebSocketEvent) => void;

interface WebSocketMessage {
  type?: string;
  message?: {
    event: WebSocketEventType;
    data: WebSocketEventData;
  };
}

declare global {
  interface Window {
    __chatwootWsManager?: ChatwootWebSocket;
  }
}

class ChatwootWebSocket {
  private ws: WebSocket | null = null;
  private callbacks: Map<string, WebSocketCallback[]> = new Map();
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;
  private reconnectDelay = 1000;
  private pubsubToken: string | null = null;
  private isConnecting = false;

  connect(pubsubToken?: string): void {
    const token = pubsubToken ?? currentPubsubToken ?? ChatwootStorage.getPubsubToken();

    if (!token) {
      console.warn('No pubsub token available for WebSocket connection');
      return;
    }

    if (this.ws) {
      const state = this.ws.readyState;
      if (state === WebSocket.OPEN || state === WebSocket.CONNECTING) {
        console.log('WebSocket already connected/connecting');
        return;
      }
      this.ws.close();
      this.ws = null;
    }

    if (this.isConnecting) {
      console.log('WebSocket connection already in progress');
      return;
    }

    this.pubsubToken = token;
    this.isConnecting = true;

    try {
      this.ws = new WebSocket(CHATWOOT_CONFIG.WS_URL);

      this.ws.onopen = (): void => {
        console.log('✅ Chatwoot WebSocket connected');
        this.isConnecting = false;
        this.reconnectAttempts = 0;
        this.subscribe();
      };

      this.ws.onmessage = (event: MessageEvent): void => {
        try {
          const data = JSON.parse(event.data as string) as WebSocketMessage;

          if (data.type === 'ping' || data.type === 'welcome' || data.type === 'confirm_subscription') {
            return;
          }

          if (data.type === 'reject_subscription') {
            console.error('WebSocket: Subscription REJECTED', data);
            return;
          }

          if (data.message) {
            const wsEvent: WebSocketEvent = {
              event: data.message.event,
              data: data.message.data,
            };
            this.emit(wsEvent);
          }
        } catch (error) {
          console.error('Error parsing WebSocket message:', error);
        }
      };

      this.ws.onerror = (): void => {
        console.error('WebSocket error');
        this.isConnecting = false;
      };

      this.ws.onclose = (): void => {
        console.log('WebSocket disconnected');
        this.isConnecting = false;
        this.ws = null;
        this.attemptReconnect();
      };
    } catch (error) {
      console.error('Failed to create WebSocket:', error);
      this.isConnecting = false;
    }
  }

  private subscribe(): void {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN || !this.pubsubToken) {
      console.warn('Cannot subscribe - WS not ready or no token');
      return;
    }

    const subscribeMessage = {
      command: 'subscribe',
      identifier: JSON.stringify({
        channel: 'RoomChannel',
        pubsub_token: this.pubsubToken,
      }),
    };

    this.ws.send(JSON.stringify(subscribeMessage));
  }

  private attemptReconnect(): void {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      console.log('Max reconnect attempts reached');
      return;
    }

    this.reconnectAttempts++;
    const delay = this.reconnectDelay * Math.pow(2, this.reconnectAttempts - 1);

    setTimeout(() => {
      if (this.pubsubToken) {
        this.connect(this.pubsubToken);
      }
    }, delay);
  }

  private emit(event: WebSocketEvent): void {
    const callbacks = this.callbacks.get(event.event) ?? [];
    const allCallbacks = this.callbacks.get('*') ?? [];
    const totalCallbacks = [...callbacks, ...allCallbacks];

    totalCallbacks.forEach((cb) => {
      try {
        cb(event);
      } catch (error) {
        console.error('Error in WebSocket callback:', error);
      }
    });
  }

  on(event: WebSocketEventType | '*', callback: WebSocketCallback): void {
    const existing = this.callbacks.get(event) ?? [];
    if (!existing.includes(callback)) {
      this.callbacks.set(event, [...existing, callback]);
    }
  }

  off(event: WebSocketEventType | '*', callback: WebSocketCallback): void {
    const existing = this.callbacks.get(event) ?? [];
    this.callbacks.set(event, existing.filter((cb) => cb !== callback));
  }

  disconnect(): void {
    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }
    this.callbacks.clear();
    this.reconnectAttempts = this.maxReconnectAttempts;
    this.isConnecting = false;
    this.pubsubToken = null;
  }

  isConnected(): boolean {
    return this.ws?.readyState === WebSocket.OPEN;
  }
}

function getOrCreateWebSocketManager(): ChatwootWebSocket {
  if (typeof window !== 'undefined') {
    if (!window.__chatwootWsManager) {
      window.__chatwootWsManager = new ChatwootWebSocket();
    }
    return window.__chatwootWsManager;
  }
  return new ChatwootWebSocket();
}

export const chatwootWebSocket = getOrCreateWebSocketManager();

// ============ UTILITY FUNCTIONS ============
export function formatMessageTime(timestamp: number | string): string {
  const date = new Date(typeof timestamp === 'number' ? timestamp * 1000 : timestamp);
  const now = new Date();
  const diff = now.getTime() - date.getTime();
  const days = Math.floor(diff / (1000 * 60 * 60 * 24));

  if (days === 0) {
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  } else if (days === 1) {
    return 'Yesterday';
  } else if (days < 7) {
    return date.toLocaleDateString([], { weekday: 'short' });
  }
  return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
}

export function isUserMessage(message: ChatwootMessage): boolean {
  if (typeof message.message_type === 'number') {
    return message.message_type === 0;
  }
  return message.message_type === 'incoming';
}

export function getUnreadCount(conversation: ChatwootConversation): number {
  if (conversation.unread_count !== undefined) {
    return conversation.unread_count;
  }

  const agentMessages = conversation.messages.filter(
    (m) => !isUserMessage(m) && !m.private
  );

  if (!conversation.contact_last_seen_at) {
    return agentMessages.length;
  }

  const lastSeen = conversation.contact_last_seen_at;
  return agentMessages.filter((m) => {
    const msgTime =
      typeof m.created_at === 'number' ? m.created_at : new Date(m.created_at).getTime() / 1000;
    return msgTime > lastSeen;
  }).length;
}

export function getLastMessage(conversation: ChatwootConversation): ChatwootMessage | null {
  if (!conversation.messages || conversation.messages.length === 0) {
    return null;
  }

  const publicMessages = conversation.messages.filter((m) => !m.private);
  return publicMessages[publicMessages.length - 1] ?? null;
}

// ============ SORTING ============
function getConversationLastActivity(conv: ChatwootConversation): number {
  if (conv.updated_at) {
    return typeof conv.updated_at === 'number' ? conv.updated_at : new Date(conv.updated_at).getTime() / 1000;
  }
  
  const lastMsg = getLastMessage(conv);
  if (lastMsg) {
    return typeof lastMsg.created_at === 'number' 
      ? lastMsg.created_at 
      : new Date(lastMsg.created_at).getTime() / 1000;
  }
  
  if (conv.created_at) {
    return typeof conv.created_at === 'number' ? conv.created_at : new Date(conv.created_at).getTime() / 1000;
  }
  
  return 0;
}

export function sortConversationsByRecent(conversations: ChatwootConversation[]): ChatwootConversation[] {
  return [...conversations].sort((a, b) => {
    const aTime = getConversationLastActivity(a);
    const bTime = getConversationLastActivity(b);
    return bTime - aTime;
  });
}

// ============ DASHBOARD UTILITIES ============

export interface DashboardStats {
  total: number;
  open: number;
  resolved: number;
  unread: number;
  recentActivity: number | null;
}

export async function getDashboardStats(): Promise<DashboardStats> {
  try {
    const result = await getConversations();
    if (!result.success || !result.data) {
      return { total: 0, open: 0, resolved: 0, unread: 0, recentActivity: null };
    }

    const conversations = result.data;
    return {
      total: conversations.length,
      open: conversations.filter((c) => c.status === 'open').length,
      resolved: conversations.filter((c) => c.status === 'resolved').length,
      unread: conversations.reduce((sum, c) => sum + getUnreadCount(c), 0),
      recentActivity: conversations[0]?.updated_at ?? null,
    };
  } catch (error) {
    console.error('Failed to get dashboard stats:', error);
    return { total: 0, open: 0, resolved: 0, unread: 0, recentActivity: null };
  }
}

export async function searchConversations(query: string): Promise<ChatwootConversation[]> {
  try {
    const result = await getConversations();
    if (!result.success || !result.data) return [];

    const lowerQuery = query.toLowerCase();
    return result.data.filter((conv) => {
      const lastMsg = getLastMessage(conv);
      const contentMatch = lastMsg?.content?.toLowerCase().includes(lowerQuery);
      const nameMatch = conv.contact?.name?.toLowerCase().includes(lowerQuery);
      const emailMatch = conv.contact?.email?.toLowerCase().includes(lowerQuery);

      return contentMatch || nameMatch || emailMatch;
    });
  } catch (error) {
    console.error('Failed to search conversations:', error);
    return [];
  }
}

export async function getConversationsByFilter(
  filter: 'all' | 'open' | 'resolved' = 'all'
): Promise<ChatwootConversation[]> {
  try {
    const result = await getConversations();
    if (!result.success || !result.data) return [];

    const sorted = sortConversationsByRecent(result.data);

    switch (filter) {
      case 'open':
        return sorted.filter((c) => c.status === 'open');
      case 'resolved':
        return sorted.filter((c) => c.status === 'resolved');
      default:
        return sorted;
    }
  } catch (error) {
    console.error('Failed to get conversations by filter:', error);
    return [];
  }
}

export async function toggleConversationStatus(
  conversationId: number,
  userId?: string
): Promise<ApiResponse<ChatwootConversation>> {
  const sourceId = userId ?? getCurrentUserId();

  if (!sourceId) {
    return { success: false, error: 'No user_id available.' };
  }

  const endpoint = `/contacts/${sourceId}/conversations/${conversationId}/toggle_status`;
  console.log('[toggleConversationStatus]', endpoint);

  return apiRequest<ChatwootConversation>(endpoint, { method: 'POST', body: JSON.stringify({}) });
}

export async function toggleConversationTyping(
  conversationId: number,
  status: 'on' | 'off' = 'on',
  userId?: string
): Promise<ApiResponse<void>> {
  const sourceId = userId ?? getCurrentUserId();

  if (!sourceId) {
    return { success: false, error: 'No user_id available.' };
  }

  const endpoint = `/contacts/${sourceId}/conversations/${conversationId}/toggle_typing`;
  console.log('[toggleConversationTyping]', { endpoint, status });

  return apiRequest<void>(endpoint, {
    method: 'POST',
    body: JSON.stringify({ typing_status: status }),
  });
}

export async function sendMessageWithAttachments(
  conversationId: number,
  content: string,
  files: File[],
  userId?: string
): Promise<ApiResponse<ChatwootMessage>> {
  const sourceId = userId ?? getCurrentUserId();

  if (!sourceId) {
    return { success: false, error: 'No user_id available.' };
  }

  const formData = new FormData();
  formData.append('content', content);
  formData.append('echo_id', `msg_${Date.now()}`);

  files.forEach((file, index) => {
    if (file.size > 40 * 1024 * 1024) {
      console.warn(`File ${file.name} exceeds 40MB limit, skipping`);
      return;
    }
    formData.append(`attachments[${index}]`, file);
  });

  const endpoint = `/contacts/${sourceId}/conversations/${conversationId}/messages`;
  const url = `${CHATWOOT_CONFIG.API_URL}${endpoint}`;
  const pubsubToken = ChatwootStorage.getPubsubToken() ?? currentPubsubToken;

  const headers: Record<string, string> = {};
  if (pubsubToken) {
    headers['X-Auth-Token'] = pubsubToken;
  }

  try {
    const response = await fetch(url, {
      method: 'POST',
      headers,
      body: formData,
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error(`Chatwoot API Error [${response.status}]:`, errorText);
      return {
        success: false,
        error: `API Error: ${response.status} - ${errorText}`,
      };
    }

    const data = await response.json() as ChatwootMessage;
    return { success: true, data };
  } catch (error) {
    console.error('Failed to send message with attachments:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Network request failed',
    };
  }
}

// ============ NOTIFICATION ============

let notificationAudio: HTMLAudioElement | null = null;

export function initNotificationSound(soundUrl?: string): void {
  try {
    // Use a default notification sound or custom URL
    const url = soundUrl || 'data:audio/wav;base64,UklGRiYAAABXQVZFZm10IBAAAAABAAEAQB8AAAB9AAACABAAZGF0YQIAAAAAAA==';
    notificationAudio = new Audio(url);
    notificationAudio.volume = 0.5;
    console.log('[Notification] Sound initialized');
  } catch (error) {
    console.error('Failed to initialize notification sound:', error);
  }
}

export function playNotificationSound(): void {
  if (notificationAudio) {
    try {
      notificationAudio.currentTime = 0;
      notificationAudio.play().catch((err) => {
        console.log('Autoplay blocked:', err.message);
      });
    } catch (error) {
      console.error('Error playing sound:', error);
    }
  }
}

// ============ DEBUGGING UTILITIES ============

export function debugLogConversation(conversation: ChatwootConversation): void {
  console.group(`📋 Conversation ${conversation.id}`);
  console.log('Status:', conversation.status);
  console.log('Contact:', conversation.contact?.name);
  console.log('Messages:', conversation.messages?.length ?? 0);
  console.log('Unread:', getUnreadCount(conversation));
  console.log('Last Activity:', formatMessageTime(getConversationLastActivity(conversation)));
  console.log('Created:', new Date((conversation.created_at ?? 0) * 1000).toLocaleString());
  console.groupEnd();
}

export function debugLogMessage(message: ChatwootMessage): void {
  console.log('💬 Message', {
    id: message.id,
    from: message.sender?.name,
    type: message.message_type,
    content: message.content.substring(0, 50),
    attachments: message.attachments?.length ?? 0,
    timestamp: formatMessageTime(message.created_at),
  });
}

export function debugDashboardStats(stats: DashboardStats): void {
  console.group('📊 Dashboard Stats');
  console.log('Total Conversations:', stats.total);
  console.log('Open:', stats.open);
  console.log('Resolved:', stats.resolved);
  console.log('Unread Messages:', stats.unread);
  console.log('Recent Activity:', stats.recentActivity ? new Date(stats.recentActivity * 1000).toLocaleString() : 'None');
  console.groupEnd();
}
